import useWatch from "@core-ui/hooks/useWatch";
// biome-ignore lint/style/noRestrictedImports: pre-existing emotion usage in this file
import styled from "@emotion/styled";
import { type DropOptions, type NodeModel, Tree } from "@minoru/react-dnd-treeview";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ItemLink } from "../../../NavigationLinks";
import DragTreeTransformer from "../logic/DragTreeTransformer";
import getOpenItemsIds from "../logic/getOpenItemsIds";
import NavItem from "./NavItem";

type handleOnDropType = (
	draggedItemPath: string,
	newTree: NodeModel<ItemLink>[],
	fetchComplete: () => void,
) => Promise<void> | void;

interface LevNavDragTreeProps {
	items: NodeModel<ItemLink>[];
	onDrop?: handleOnDropType;
	closeNavigation?: () => void;
	canDrag?: boolean;
	className?: string;
	onOpenChange?: (id: number | string, open: boolean, items: NodeModel<ItemLink>[]) => void;
}

const LevNavDragTree = (props: LevNavDragTreeProps) => {
	const { items = [], onDrop, closeNavigation, canDrag = true, className, onOpenChange } = props;
	const [initialOpen, setInitialOpen] = useState(() => new Set<number | string>(getOpenItemsIds(items)));
	const [draggedItemPath, setDraggedItemPath] = useState<string>();

	// To keep toggleOpen stable without deps
	const itemsRef = useRef(items);
	itemsRef.current = items;
	const onOpenChangeRef = useRef(onOpenChange);
	useEffect(() => {
		onOpenChangeRef.current = onOpenChange;
	}, [onOpenChange]);

	useWatch(() => {
		setInitialOpen(new Set<number | string>(getOpenItemsIds(items)));
	}, [items]);

	const toggleOpen = useCallback((id: number | string, shouldOpen: boolean) => {
		setInitialOpen((prev) => {
			const next = new Set(prev);
			if (shouldOpen) {
				next.add(id);
			} else {
				next.delete(id);
				const descendants: (number | string)[] = [];
				const collect = (parentId: number | string) => {
					for (const node of itemsRef.current) {
						if (node.parent === parentId) {
							descendants.push(node.id);
							collect(node.id);
						}
					}
				};
				collect(id);
				for (const d of descendants) next.delete(d);
			}
			// Guard: skip saving if items haven't loaded yet (prevents wiping state on mount)
			if (itemsRef.current.length === 0) return next;

			onOpenChangeRef.current?.(id, shouldOpen, itemsRef.current);
			return next;
		});
	}, []);

	const onDropHandler = useCallback(
		(tree: NodeModel<ItemLink>[], { dropTargetId }: DropOptions<ItemLink>) => {
			const addToInitialOpen = () => {
				toggleOpen(dropTargetId, true);
			};
			void onDrop(draggedItemPath, tree, addToInitialOpen);
			setDraggedItemPath(undefined);
		},
		[draggedItemPath, onDrop, toggleOpen],
	);

	const handleCanDrop = useCallback((_, { dragSource, dropTargetId }: DropOptions<ItemLink>) => {
		if (dragSource?.parent === dropTargetId) return true;
	}, []);

	const handleDragEnd = useCallback(() => setDraggedItemPath(undefined), []);
	const handleDragStart = useCallback((tree: NodeModel<ItemLink>) => setDraggedItemPath(tree.data.ref.path), []);
	const canDragCheck = useCallback(() => canDrag, [canDrag]);

	const initialOpenArray = useMemo(() => Array.from(initialOpen), [initialOpen]);

	return (
		<div className={className}>
			<Tree<ItemLink>
				canDrag={canDragCheck}
				canDrop={handleCanDrop}
				classes={{
					root: "tree-root",
					draggingSource: "dragging-source",
					placeholder: "placeholder-container",
				}}
				dropTargetOffset={6}
				initialOpen={initialOpenArray}
				insertDroppableFirst={false}
				onDragEnd={handleDragEnd}
				onDragStart={handleDragStart}
				onDrop={onDropHandler}
				placeholderRender={(_, { depth }) => {
					return <div className={`placeholder depth-${depth}`} />;
				}}
				render={(node, params) => (
					<NavItem
						closeNavigation={closeNavigation}
						draggedItemPath={draggedItemPath}
						node={node}
						params={params}
						toggleOpen={toggleOpen}
					/>
				)}
				rootId={DragTreeTransformer.getRootId()}
				sort={false}
				tree={items}
			/>
		</div>
	);
};

export default memo(
	styled(LevNavDragTree)`
		height: 100%;

		* {
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
			user-select: none;
		}

		li {
			margin-bottom: 0;
		}

		.tree-root {
			height: 100%;
			padding-bottom: 10px;
		}

		.dragging-source {
			opacity: 0.3;
		}

		.placeholder-container {
			position: relative;

			div.placeholder {
				top: 0;
				right: 0;
				height: 2px;
				position: absolute;
				left: var(--left-padding);
				transform: translateY(-50%);
				background-color: var(--color-primary-general);
			}
		}
	`,
);
